/*
  nsd-relewise.js
  --------------------------------------------------------------------------
  Two jobs for every Relewise product-recommendation block (Popular products
  on the homepage, Products others viewed / Purchased with product on product
  pages, ...):

  1. Native-looking carousel controls (prev / next arrows + "x / y" page
     counter) around each recommendation element, which nsd-relewise.css
     turns into a horizontal scroll-snap track (via the `.nsd-rw-track`
     class this file adds).

  2. Initialise the Lipscore review widgets that the product cards render
     (<span class="lipscore-rating-small">). Relewise runs in light-DOM mode
     (config/settings_data.json -> componentDomMode: "light") so the widgets
     are in the normal document; we just have to tell Lipscore to scan for
     them once the tiles have been inserted.

  Relewise loads + renders asynchronously and re-renders its tiles when
  consent / personalisation changes, so a MutationObserver re-runs both jobs.

  Loaded from layout/theme.liquid on the homepage and product pages.
*/
(function () {
  'use strict';

  var CARET =
    '<svg class="icon icon-caret" viewBox="0 0 10 6" aria-hidden="true" focusable="false">' +
    '<path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/>' +
    '</svg>';

  function initLipscore() {
    try {
      if (window.lipscore && typeof window.lipscore.initWidgets === 'function') {
        window.lipscore.initWidgets();
      }
    } catch (e) {
      /* Lipscore not ready yet – a later mutation / retry will catch it. */
    }
  }

  // The custom card (templates/product.relewise-product-card.liquid) includes
  // <relewise-product-favorite-button data-nsd-fav>. That element needs its
  // `product` / `user` set as properties – copy them from the parent tile.
  function hydrateFavorites(track) {
    var buttons = track.querySelectorAll('relewise-product-favorite-button[data-nsd-fav]');
    Array.prototype.forEach.call(buttons, function (btn) {
      var tile = btn.closest('relewise-product-tile');
      if (!tile) return;

      // Drop any heart Relewise injected itself – it anchors to the wrong
      // box in light-DOM mode. Ours lives inside .relewise-card and is
      // positioned there.
      Array.prototype.forEach.call(
        tile.querySelectorAll('relewise-product-favorite-button:not([data-nsd-fav])'),
        function (n) {
          n.remove();
        },
      );

      if (btn.dataset.nsdFavReady || !tile.product) return;
      btn.product = tile.product;
      if (tile.user) btn.user = tile.user;
      btn.removeAttribute('hidden'); // the component re-hides itself if anon
      btn.dataset.nsdFavReady = 'true';
    });
  }

  // Every Relewise product-recommendation element shares the same grid /
  // tile structure, so they all get the same carousel treatment. The value
  // is the section title shown above the row – edit these to taste.
  var REC_TITLES = {
    'relewise-popular-products': '',
    'relewise-personal-products': 'Recommended for you',
    'relewise-recently-viewed-products': 'Recently viewed',
    'relewise-products-viewed-after-viewing-product': 'You may also like',
    'relewise-purchased-with-product': 'Frequently bought together',
    'relewise-purchased-with-multiple-products': 'Frequently bought together',
    'relewise-search-term-based-products': 'You might also like',
  };
  var REC_SELECTOR = Object.keys(REC_TITLES).join(',');

  // These blocks run edge-to-edge instead of capped at the theme page width.
  var FULL_WIDTH = ['relewise-popular-products'];

  function enhance(track) {
    if (!track || track.dataset.nsdRwReady) return;
    track.dataset.nsdRwReady = 'true';
    track.classList.add('nsd-rw-track');

    var tag = track.tagName.toLowerCase();

    // Wrapper – a full-viewport-width white band. Its contents are re-capped
    // at the theme page width (like sections/related-products), unless this
    // block is in FULL_WIDTH, in which case the row spans the viewport.
    var wrapper = document.createElement('div');
    wrapper.className = 'nsd-rw-carousel';
    if (FULL_WIDTH.indexOf(tag) !== -1) wrapper.classList.add('nsd-rw-carousel--full');
    track.parentNode.insertBefore(wrapper, track);

    // Title
    var titleText = REC_TITLES[tag];
    if (titleText) {
      var title = document.createElement('h2');
      title.className = 'nsd-rw-carousel__title h1'; // h1 size, like related-products
      title.textContent = titleText;
      wrapper.appendChild(title);
    }

    wrapper.appendChild(track);

    // Controls
    var buttons = document.createElement('div');
    buttons.className = 'nsd-rw-carousel__buttons';
    buttons.innerHTML =
      '<button type="button" class="nsd-rw-carousel__button nsd-rw-carousel__button--prev" aria-label="Previous">' +
      CARET +
      '</button>' +
      '<span class="nsd-rw-carousel__counter" aria-hidden="true">' +
      '<span class="nsd-rw-carousel__counter-current">1</span>&nbsp;/&nbsp;' +
      '<span class="nsd-rw-carousel__counter-total">1</span>' +
      '</span>' +
      '<button type="button" class="nsd-rw-carousel__button nsd-rw-carousel__button--next" aria-label="Next">' +
      CARET +
      '</button>';
    wrapper.appendChild(buttons);

    var prev = buttons.querySelector('.nsd-rw-carousel__button--prev');
    var next = buttons.querySelector('.nsd-rw-carousel__button--next');
    var current = buttons.querySelector('.nsd-rw-carousel__counter-current');
    var total = buttons.querySelector('.nsd-rw-carousel__counter-total');

    function pageWidth() {
      // Snap to whole cards: use the first tile's outer width when we can,
      // otherwise fall back to the viewport of the track.
      var first = track.children[0];
      if (first) {
        var styles = getComputedStyle(track);
        var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        var tileWidth = first.getBoundingClientRect().width + gap;
        var per = Math.max(1, Math.round(track.clientWidth / tileWidth));
        return per * tileWidth;
      }
      return track.clientWidth;
    }

    function sync() {
      var max = track.scrollWidth - track.clientWidth;
      // A few px of slack so sub-pixel rounding doesn't leave a "scroller"
      // with one full page – treat that as static (render as a grid).
      var isStatic = max <= 4;
      wrapper.toggleAttribute('data-nsd-rw-static', isStatic);
      if (isStatic) return;

      var step = pageWidth() || track.clientWidth;
      var pages = Math.max(1, Math.ceil(track.scrollWidth / track.clientWidth));
      var page = Math.min(pages, Math.round(track.scrollLeft / step) + 1);

      current.textContent = page;
      total.textContent = pages;
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= max - 1;
    }

    prev.addEventListener('click', function () {
      track.scrollBy({ left: -(pageWidth() || track.clientWidth), behavior: 'smooth' });
    });
    next.addEventListener('click', function () {
      track.scrollBy({ left: pageWidth() || track.clientWidth, behavior: 'smooth' });
    });

    var raf;
    track.addEventListener(
      'scroll',
      function () {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(sync);
      },
      { passive: true },
    );
    window.addEventListener('resize', sync);

    // Re-run on every tile change (Relewise re-render, lazy content, etc.)
    var settle;
    function onChange() {
      sync();
      hydrateFavorites(track);
      clearTimeout(settle);
      settle = setTimeout(initLipscore, 150);
    }

    var mo = new MutationObserver(onChange);
    mo.observe(track, { childList: true, subtree: true });

    // Relewise also emits this once a recommendation has loaded.
    track.addEventListener('relewise-ui-components:recommendation-state-changed', function (e) {
      if (e && e.detail && e.detail.loading === false) onChange();
    });

    onChange();
    // A few spaced retries: Lipscore may still be downloading, and the tile's
    // `product` / `user` properties can land a tick after the markup does.
    [600, 1500, 3000].forEach(function (t) {
      setTimeout(onChange, t);
    });
  }

  // The Relewise app script loads async, then renders tiles async, and a
  // page can hold more than one block. Poll for ~20s and enhance each
  // recommendation element as soon as it has content.
  function boot() {
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      var els = document.querySelectorAll(REC_SELECTOR);
      Array.prototype.forEach.call(els, function (track) {
        if (!track.dataset.nsdRwReady && track.children.length > 0) {
          enhance(track);
        }
      });
      if (tries > 40) clearInterval(timer); // give up after ~20s
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
